import React from 'react';
import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { Trader } from '../../../types/trader';
import { PaymentSystem } from '../../../types/common/payment';
import type { RequisiteFiltersState } from '../hooks/useRequisites';
import SearchableSelect from '@/components/SearchableSelect';

interface RequisiteFiltersProps {
  filters: RequisiteFiltersState;
  traders: Trader[];
  paymentSystems: PaymentSystem[];
  onFilterChange: (field: keyof RequisiteFiltersState, value: unknown) => void;
  onSearch: () => void;
  onReset: () => void;
}

export default function RequisiteFilters({
  filters,
  traders,
  paymentSystems,
  onFilterChange,
  onSearch,
  onReset,
}: RequisiteFiltersProps) {
  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            variant="outlined"
            label="Поиск по названию и номеру"
            value={filters.name_or_number}
            onChange={(e) => onFilterChange('name_or_number', e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={1.5}>
          <SearchableSelect label="Трейдер" value={filters.trader_id} options={traders.map((item) => ({ id: item.id, label: item.name }))} onChange={(value) => onFilterChange('trader_id', value)} />
        </Grid>
        <Grid item xs={12} sm={6} md={1.5}>
          <SearchableSelect label="Платёжная система" value={filters.payment_system_id} options={paymentSystems.map((item) => ({ id: item.id, label: item.token }))} onChange={(value) => onFilterChange('payment_system_id', value)} />
        </Grid>
        <Grid item xs={12} sm={6} md={1.5}>
          <FormControl fullWidth>
            <InputLabel>Статус</InputLabel>
            <Select
              label="Статус"
              value={filters.is_active}
              onChange={(event) => onFilterChange('is_active', event.target.value)}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="true">Активные</MenuItem>
              <MenuItem value="false">Неактивные</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={1.5}>
          <FormControl fullWidth>
            <InputLabel>UPI-блокировка</InputLabel>
            <Select
              label="UPI-блокировка"
              value={filters.is_blocked_by_trader}
              onChange={(event) =>
                onFilterChange('is_blocked_by_trader', event.target.value)
              }
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="true">Заблокированные</MenuItem>
              <MenuItem value="false">Без блокировки</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel>Сортировка лимитов</InputLabel>
            <Select
              label="Сортировка лимитов"
              value={filters.transaction_limit_sort}
              onChange={(event) => onFilterChange('transaction_limit_sort', event.target.value)}
            >
              <MenuItem value="">По умолчанию</MenuItem>
              <MenuItem value="asc">Сначала маленькие</MenuItem>
              <MenuItem value="desc">Сначала большие</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box display="flex" gap={1}>
            <Button variant="outlined" onClick={onSearch} fullWidth sx={{ height: '56px' }}>
              Применить
            </Button>
            <Button
              variant="outlined"
              onClick={onReset}
              startIcon={<ClearIcon />}
              fullWidth
              sx={{ height: '56px' }}
            >
              Сбросить
            </Button>
          </Box>
        </Grid>
      </Grid>
      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Switch
            checked={filters.include_pre_deleted}
            onChange={(event) =>
              onFilterChange('include_pre_deleted', event.target.checked)
            }
          />
        }
        label="Показывать удалённые"
      />
    </Paper>
  );
}
